import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlayCircle, ArrowRight, CheckCircle2, Loader2, Sparkles, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const glass = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

function TrackedVideo({ src, videoKey, sid, refSlug, refSuffix, inviteToken, utmKey, onEnded }) {
  const maxRef = useRef(0);
  const lastSent = useRef(0);
  const send = (pct, ended = false) => {
    axios.post(`${API}/discover/track`, { session_id: sid, video: videoKey, pct: Math.round(pct * 10) / 10, ended, ref: refSlug || null, suffix: refSuffix || null, invite_token: inviteToken || null, utm_key: utmKey || null }).catch(() => {});
  };
  const onTime = (e) => {
    const v = e.currentTarget;
    if (!v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct > maxRef.current) maxRef.current = pct;
    const now = Date.now();
    if (now - lastSent.current > 8000) { lastSent.current = now; send(maxRef.current); }
  };
  return (
    <video
      src={src}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload"
      className="w-full rounded-xl bg-black aspect-video"
      onTimeUpdate={onTime}
      onPause={() => send(maxRef.current)}
      onEnded={() => { maxRef.current = 100; send(100, true); onEnded?.(); }}
      data-testid={`discover-video-${videoKey}`}
    />
  );
}

export default function Discover() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [stage, setStage] = useState(() => localStorage.getItem("discover_stage") || "sizzle"); // sizzle | form | present | done
  const [form, setForm] = useState(() => {
    try { return JSON.parse(localStorage.getItem("discover_form")) || { name: "", phone: "", email: "" }; }
    catch { return { name: "", phone: "", email: "" }; }
  });
  const [busy, setBusy] = useState(false);

  const sid = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const inv = p.get("i") || localStorage.getItem("discover_invite") || "";
    if (inv) return `inv-${inv}`.slice(0, 80);
    let s = localStorage.getItem("discover_sid");
    if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`); localStorage.setItem("discover_sid", s); }
    return s;
  }, []);
  const refSlug = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("ref") || p.get("inviteCode") || localStorage.getItem("discover_ref") || "";
    if (r) localStorage.setItem("discover_ref", r);
    return r;
  }, []);
  // The wallet suffix off the sponsor's CaryPact link — names their exact wallet.
  const refSuffix = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const s2 = p.get("s") || p.get("suffix") || localStorage.getItem("discover_suffix") || "";
    if (s2) localStorage.setItem("discover_suffix", s2);
    return s2;
  }, []);
  // Private per-recipient token from an emailed share link — lets the lead be
  // identified (name/email we sent to) and locked to the sponsor on first click.
  const inviteToken = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const i = p.get("i") || localStorage.getItem("discover_invite") || "";
    if (i) localStorage.setItem("discover_invite", i);
    return i;
  }, []);
  // Marketing campaign key (?k=) — the wallet behind it is resolved server-side.
  const utmKey = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const k = p.get("k") || localStorage.getItem("discover_utm") || "";
    if (k) localStorage.setItem("discover_utm", k);
    return k;
  }, []);

  useEffect(() => {
    axios.get(`${API}/discover/content`, { params: refSlug ? { ref: refSlug } : {} })
      .then((r) => setContent(r.data)).catch(() => setContent({}));
  }, [refSlug]);

  // Register an emailed-invite click on landing — the person shows in Prospects
  // (identified by the email we sent to) even before they watch anything.
  useEffect(() => {
    if (!inviteToken) return;
    axios.post(`${API}/discover/land`, { session_id: sid, ref: refSlug || null, suffix: refSuffix || null, invite_token: inviteToken, utm_key: utmKey || null }).catch(() => {});
  }, [inviteToken, sid, refSlug, refSuffix, utmKey]);

  const setStagePersist = (s) => { setStage(s); localStorage.setItem("discover_stage", s); };

  const submitLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/discover/lead`, { session_id: sid, ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), ref: refSlug || null, suffix: refSuffix || null, invite_token: inviteToken || null, utm_key: utmKey || null });
      localStorage.setItem("discover_form", JSON.stringify(form));
      setStagePersist("present");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save your details — please try again");
    } finally { setBusy(false); }
  };

  const startOnboarding = () => {
    const q = new URLSearchParams();
    if (refSlug) q.set("ref", refSlug);
    if (refSuffix) q.set("s", refSuffix);
    if (utmKey) q.set("k", utmKey);
    if (form.name) q.set("name", form.name.trim());
    if (form.phone) q.set("phone", form.phone.trim());
    if (form.email) q.set("email", form.email.trim());
    navigate(`/join?${q.toString()}`);
  };

  const steps = [
    { k: "sizzle", label: "Watch" },
    { k: "form", label: "Your details" },
    { k: "present", label: "Presentation" },
    { k: "done", label: "Get started" },
  ];
  const stageIdx = steps.findIndex((s) => s.k === stage);

  return (
    <div className="min-h-screen text-slate-100" style={{ background: "radial-gradient(1200px 600px at 70% -10%, rgba(64,96,255,0.18), transparent), radial-gradient(900px 500px at 0% 100%, rgba(124,58,237,0.14), transparent), #070B14" }} data-testid="discover-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#4060FF,#7C3AED)" }}><Sparkles size={17} /></div>
            <div>
              <div className="font-display font-bold tracking-tight">BotChainCP</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5" data-testid="discover-progress">
            {steps.map((s, i) => (
              <span key={s.k} className="h-1.5 rounded-full transition-all" style={{ width: i === stageIdx ? 26 : 12, background: i <= stageIdx ? "#4060FF" : "rgba(255,255,255,0.14)" }} title={s.label} />
            ))}
          </div>
        </div>

        {!content ? (
          <div className="text-center py-24"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
        ) : stage === "sizzle" ? (
          <div className={`${glass} p-6`} data-testid="discover-step-sizzle">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2"><Film size={12} /> Step 1 · 2 minutes</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-1">See what this is about</h1>
            <p className="text-sm text-slate-400 mb-4">Watch the short video below — the next step unlocks when it ends.</p>
            {content.sizzle ? (
              <TrackedVideo src={`${process.env.REACT_APP_BACKEND_URL}${content.sizzle.url}`} videoKey="sizzle" sid={sid} refSlug={refSlug} refSuffix={refSuffix} inviteToken={inviteToken} utmKey={utmKey} onEnded={() => setStagePersist("form")} />
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/40 aspect-video flex flex-col items-center justify-center text-slate-500 gap-2" data-testid="discover-sizzle-missing">
                <PlayCircle size={36} /> <span className="text-sm">Video is being prepared — check back shortly.</span>
              </div>
            )}
          </div>
        ) : stage === "form" ? (
          <div className={`${glass} p-6`} data-testid="discover-step-form">
            <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2"><CheckCircle2 size={15} /> Nice — you've seen the intro.</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-1">Want the full picture?</h1>
            <p className="text-sm text-slate-400 mb-5">Leave your details and the full presentation starts right away.</p>
            <form onSubmit={submitLead} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="d-name" className="text-slate-300">Your name</Label>
                <Input id="d-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Jane Smith"
                  className="mt-1 bg-white/[0.06] border-white/15 text-slate-100 placeholder:text-slate-500" data-testid="discover-name" />
              </div>
              <div>
                <Label htmlFor="d-phone" className="text-slate-300">Phone number</Label>
                <Input id="d-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required placeholder="e.g. +1 555 123 4567"
                  className="mt-1 bg-white/[0.06] border-white/15 text-slate-100 placeholder:text-slate-500" data-testid="discover-phone" />
              </div>
              <div>
                <Label htmlFor="d-email" className="text-slate-300">Email address</Label>
                <Input id="d-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="e.g. jane@email.com"
                  className="mt-1 bg-white/[0.06] border-white/15 text-slate-100 placeholder:text-slate-500" data-testid="discover-email" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 font-semibold" style={{ background: "linear-gradient(135deg,#4060FF,#7C3AED)" }} data-testid="discover-submit">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Watch the presentation <ArrowRight size={16} className="ml-1.5" /></>}
              </Button>
            </form>
          </div>
        ) : stage === "present" ? (
          <div className={`${glass} p-6`} data-testid="discover-step-present">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2"><Film size={12} /> Step 2 · The presentation</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-1">Here's how it works{form.name ? `, ${form.name.split(" ")[0]}` : ""}</h1>
            <p className="text-sm text-slate-400 mb-4">When the presentation ends, you can start your onboarding.</p>
            {content.present ? (
              <TrackedVideo src={`${process.env.REACT_APP_BACKEND_URL}${content.present.url}`} videoKey="present" sid={sid} refSlug={refSlug} refSuffix={refSuffix} inviteToken={inviteToken} utmKey={utmKey} onEnded={() => setStagePersist("done")} />
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/40 aspect-video flex flex-col items-center justify-center text-slate-500 gap-2" data-testid="discover-present-missing">
                <PlayCircle size={36} /> <span className="text-sm">Video is being prepared — check back shortly.</span>
              </div>
            )}
          </div>
        ) : (
          <div className={`${glass} p-8 text-center`} data-testid="discover-step-done">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={28} /></div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2">You've seen it all{form.name ? `, ${form.name.split(" ")[0]}` : ""}.</h1>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Ready to get set up? Onboarding takes you step by step — your details carry over.</p>
            <Button onClick={startOnboarding} className="h-12 px-8 font-semibold text-base" style={{ background: "linear-gradient(135deg,#4060FF,#7C3AED)" }} data-testid="discover-start-onboarding">
              Start onboarding <ArrowRight size={17} className="ml-2" />
            </Button>
            <button onClick={() => setStagePersist("present")} className="block mx-auto mt-4 text-xs text-slate-500 hover:text-slate-300" data-testid="discover-rewatch">Rewatch the presentation</button>
          </div>
        )}

        <div className="text-center text-[11px] text-slate-600 mt-8">BotChainCP · CaryPact operations</div>
      </div>
    </div>
  );
}
